'use client';

import { Trans } from '@kit/ui/trans';

import { FadeIn, SlideIn } from './animations';

const FONT_BEBAS = 'var(--font-bebas-neue), Impact, sans-serif';
const FONT_INTER = 'var(--font-inter-agency), -apple-system, sans-serif';

const VIDEO_SRC = '/agency/videos/agence2.mp4';

const companyLogos = [
  { name: 'Paris Opera Ballet', src: '/agency/logos/paris_opera_ballet.png' },
  { name: 'The Royal Ballet', src: '/agency/logos/royal_ballet.jpg' },
  { name: 'American Ballet Theatre', src: '/agency/logos/abt.png' },
  { name: 'Teatro alla Scala', src: '/agency/logos/scala.png' },
  { name: 'Staatsoper Berlin', src: '/agency/logos/staatsoper.png' },
  {
    name: 'Dutch National Ballet',
    src: '/agency/logos/dutch_national_ballet.jpg',
  },
];

export function TheDancersSection() {
  return (
    <section
      className="border-border border-t py-32 md:py-44"
      data-testid="dancers-section"
    >
      <div className="mx-auto mb-20 max-w-6xl px-8 md:px-16">
        <div className="flex flex-col gap-16 md:flex-row md:items-center md:gap-24">
          <div className="min-w-0 flex-1">
            <FadeIn>
              <p
                className="text-muted-foreground mb-8 text-xs tracking-[0.3em] uppercase"
                style={{ fontFamily: FONT_INTER }}
              >
                <Trans i18nKey="agency:dancers.eyebrow" />
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h2
                className="text-foreground max-w-2xl leading-tight"
                style={{
                  fontFamily: FONT_BEBAS,
                  fontWeight: 300,
                  fontSize: 'clamp(2rem, 4vw, 3.8rem)',
                }}
              >
                <Trans i18nKey="agency:dancers.headlineBefore" />
                <br />
                <em
                  style={{
                    fontFamily: FONT_INTER,
                    fontStyle: 'italic',
                    fontWeight: 300,
                  }}
                >
                  <Trans i18nKey="agency:dancers.headlineEm" />
                </em>
              </h2>
            </FadeIn>
            <FadeIn delay={0.15}>
              <p
                className="text-muted-foreground mt-8 max-w-xl leading-relaxed"
                style={{
                  fontFamily: FONT_INTER,
                  fontWeight: 300,
                  fontSize: '0.88rem',
                  letterSpacing: '0.04em',
                }}
              >
                <Trans i18nKey="agency:dancers.body" />
              </p>
            </FadeIn>
          </div>

          <FadeIn
            delay={0.2}
            className="flex flex-shrink-0 justify-center md:justify-end"
          >
            <div
              className="relative overflow-hidden"
              style={{
                width: 'clamp(156px, 26vw, 264px)',
                aspectRatio: '9/16',
              }}
            >
              <video
                src={VIDEO_SRC}
                autoPlay
                muted
                loop
                playsInline
                style={{
                  position: 'absolute',
                  top: '-50px',
                  left: 0,
                  width: '100%',
                  height: 'calc(100% + 50px)',
                  objectFit: 'cover',
                  objectPosition: 'center center',
                }}
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 55%)',
                }}
              />
              <div className="absolute bottom-0 left-0 p-4">
                <p
                  className="text-[9px] tracking-[0.35em] text-white/60 uppercase"
                  style={{ fontFamily: FONT_INTER }}
                >
                  {/* i18n-ignore brand name */}
                  Ballee Agency <Trans i18nKey="agency:dancers.videoCaption" />
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>

      <div className="mx-auto mt-20 max-w-6xl px-8 md:px-16">
        <FadeIn>
          <p
            className="text-foreground/60 mb-12 max-w-2xl leading-none"
            style={{
              fontFamily: FONT_BEBAS,
              fontWeight: 300,
              fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
              fontStyle: 'italic',
            }}
          >
            <Trans i18nKey="agency:dancers.institutions" />
          </p>
        </FadeIn>
        <div
          className="bg-border grid grid-cols-2 gap-px md:grid-cols-3"
          data-testid="companies-list"
        >
          {companyLogos.map((co, i) => (
            <SlideIn key={co.name} delay={0.07 * i}>
              <div
                className="group bg-background flex items-center justify-center px-3 py-8 md:px-8 md:py-10"
                style={{ minHeight: '180px' }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={co.src}
                  alt={co.name}
                  data-testid={`company-${i}`}
                  style={{
                    height: '144px',
                    width: '100%',
                    maxWidth: '400px',
                    objectFit: 'contain',
                    filter: 'grayscale(1) opacity(0.5)',
                    transition: 'filter 0.5s ease',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.filter = 'grayscale(0) opacity(1)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.filter =
                      'grayscale(1) opacity(0.45)')
                  }
                />
              </div>
            </SlideIn>
          ))}
        </div>
        <FadeIn delay={0.5}>
          <p
            className="text-muted-foreground/40 mt-8 text-xs italic"
            style={{ fontFamily: FONT_INTER }}
          >
            <Trans i18nKey="agency:dancers.andMore" />
          </p>
        </FadeIn>
      </div>
    </section>
  );
}
